/*
 * @providesModule BalanceBarV2
 *
 */
import {IAnimatedBurstShowerProps} from '@skillz/ui';
import {
  CURRENCY_BAR_CASH,
  CURRENCY_BAR_CHAT,
  CURRENCY_BAR_TICKETZ,
  CURRENCY_BAR_Z,
} from 'AWSConstants';
import AWSManager from 'AWSManager';
import {IAnimatedShowerProps} from 'AnimatedShower';
import AssetHelper from 'AssetHelper';
import styles from 'BalanceBarV2Style';
import ClientPropertyConstants from 'ClientPropertyConstants';
import GlobalEventEmitter from 'GlobalEventEmitter';
import constants from 'HomeScreenConstants';
import I18n from 'INTL';
import NativeNavigation from 'NativeNavigation';
import NavigationManager from 'NavigationManager';
import Optional from 'Optional';
import SKZConstants from 'SKZConstants';
import Sizes from 'Sizes';
import {
  CashCountingLabel,
  MedalCountingLabel,
  TicketzCountingLabel,
  ZCountingLabel,
} from 'SkillzCountingLabels';
import SkillzSVG from 'SkillzSVG';
import SkillzImage from 'SkillzImage';
import SkillzStyle, {chatIconSvg} from 'SkillzStyle';
import _ from 'lodash';
import React from 'react';
import {
  Animated,
  EmitterSubscription,
  ImageSourcePropType,
  LayoutChangeEvent,
  NativeModules,
  Platform,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
  findNodeHandle,
} from 'react-native';
import {pickTextColorBasedOnBgColor} from '@skillz-rn/utils';
import {TelemetryEventName} from 'TelemetryConstants';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';

const RCTUIManager = NativeModules.UIManager;

const TEST_IDS = {
  BALANCE_BAR: 'balance_bar',
  CHAT_BADGE: 'chat_badge',
  CHAT_BUTTON: 'chat_button',
};

const CurrencyTypes = SKZConstants.CurrencyTypes;

type SKZCurrencyTypes = 'CASH' | 'TICKETZ' | 'Z' | 'MEDAL';

type ShowerAnimationConfigOptions = {
  x: number;
  y: number;
  iconCount: number | string;
  currencyCount: number | string;
};

export type chatSpotlightProps = {
  spotlightCornerRadiusChat: number;
  spotlightRefChat: number;
};

interface IViewStylePlusTextColor extends ViewStyle {
  color?: string;
}

type Props = {
  shouldAnimate?: boolean;
  forceAnimation?: boolean;
  animationCurve?: string;
  animationDuration?: number;
  animationPrecisions?: {z: number; cash: number; ticketz: number};
  cashBalance: number;
  zBalance: number;
  ticketzBalance: number;
  medalBalance: number;
  currencyLayout?: (arg1: {
    nativeEvent: {
      layout: {
        x: number;
        y: number;
        width: number;
        height: number;
      };
    };
  }) => void;
  borderType: 'surround' | 'divider' | 'none';
  borderColor?: string;
  currencyOrder: string[];
  style?: IViewStylePlusTextColor;
  isTouchable?: boolean;
  disableEvents?: boolean;
  identifier?: string;
  showRightSideIcon?: boolean;
  showChatBadge?: boolean;
  overrideTheming?: boolean;
  lightThemeOverride?: boolean;
  darkThemeOverride?: boolean;
  /** this function when defined is a callback method for when the chat icon is available for education */
  onEducationChatSpotlightUpdated: (props: chatSpotlightProps) => {};
  /** This is a ref that gets assigned to the chat icon for education */
  educationChatRef: React.RefObject<View> | undefined;
};

type State = {};

export const getIconURL = (): string => {
  const currentCurrency = I18n.currentCurrency();
  switch (currentCurrency) {
    case 'INR':
      return AssetHelper.pathForAssetNamed('ic_cash_skz_inr.png', false);
    default:
      return AssetHelper.pathForAssetNamed('ic_cash_usd_background.png', false);
  }
};

export const requestBurstShowerAnimation = (
  config: Array<{currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}>,
  cashRef: React.Component<any, any>,
  zRef: React.Component<any, any>,
  ticketRef: React.Component<any, any>
) => {
  const promises: Promise<IAnimatedBurstShowerProps>[] = [];
  let currencyRef: React.Component<any, any> | null = null;
  _.forEach(
    config,
    (configItem: {currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}) => {
      let currencyImg: ImageSourcePropType | undefined = undefined;
      switch (configItem.currencyType) {
        case CurrencyTypes.CASH: {
          currencyRef = cashRef;
          currencyImg = {uri: getIconURL()};
          break;
        }
        case CurrencyTypes.Z: {
          currencyImg = {
            uri: AssetHelper.pathForAssetNamed(
              SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI
            ),
          };
          currencyRef = zRef;
          break;
        }
        case CurrencyTypes.TICKETZ: {
          currencyImg = {uri: AssetHelper.pathForAssetNamed('ic_ticketz_background.png', false)};
          currencyRef = ticketRef;
          break;
        }
      }
      const promise = new Promise(
        (resolve: (prop: IAnimatedBurstShowerProps) => void, _reject: (error: Error) => void) => {
          const node = findNodeHandle(currencyRef);
          const currencyCap = configItem.currencyType == CurrencyTypes.CASH ? 7 : 9;
          if (!node) {
            console.warn('BalanceBarV2: Shower node undefined');
          }
          const getCurrencyCountNumber = (count: number | string): number => {
            if (typeof count === 'string') {
              return parseInt(count);
            }
            return count;
          };

          RCTUIManager.measure(
            node,
            (
              _x: number,
              _y: number,
              width: number,
              _height: number,
              pagePointX: number,
              pagePointY: number
            ) => {
              let endXOffset = 5;
              if (
                configItem.currencyType == CurrencyTypes.CASH &&
                Platform.OS == 'android' &&
                !ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
              ) {
                endXOffset = 10;
              }
              const widthXFallback = Number(pagePointX) + 7;
              const delayVCap = configItem.options.iconCount < 8 ? 75 : 50;
              const decrementYpoint = ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
                ? 4
                : 1;
              const scaleItems = ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
                ? [0.4, 1.5, 1.1, 1]
                : [0.4, 1.5, 1, 0.7];
              const startingX = configItem.options.x;
              const startingY = configItem.options.y;
              const endingX = width !== 0 ? Math.floor(pagePointX) - endXOffset : widthXFallback;
              const endingY = Math.floor(pagePointY) - decrementYpoint;
              const controlX = Math.max(startingX, endingX) + 100;
              const params = {
                icon: currencyImg,
                startingPoint: {x: startingX, y: startingY},
                endingPoint: {
                  x: endingX,
                  y: endingY,
                },
                iconCount:
                  getCurrencyCountNumber(configItem.options.iconCount) < currencyCap
                    ? getCurrencyCountNumber(configItem.options.iconCount)
                    : currencyCap,
                animatedValue: {
                  startingVariant: {x: 30, y: 30},
                  delayV: delayVCap,
                  duration: 600,
                  delay: 500,
                  endDelay: 170,
                  itemSize: 33,
                  scaleItems: scaleItems,
                  opacity: [0, 1],
                  control: {x: controlX, y: startingY},
                  fadeOut: false,
                  scaleUp: false,
                },
              };
              resolve(params);
            }
          );
        }
      );
      promises.push(promise);
    }
  );
  Promise.all(promises).then((showerArray: IAnimatedBurstShowerProps[]) => {
    GlobalEventEmitter.emit('commitBurstShowerAnimations', showerArray);
  });
};

export const requestShowerAnimation = (
  config: Array<{currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}>,
  cashRef: React.Component<any, any>,
  zRef: React.Component<any, any>,
  ticketRef: React.Component<any, any>
) => {
  let currencyRef: React.Component<any, any> | null = null;
  const promises: Promise<IAnimatedShowerProps>[] = [];
  _.forEach(
    config,
    (configItem: {currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}) => {
      let currencyImg: string | undefined = undefined;
      switch (configItem.currencyType) {
        case CurrencyTypes.CASH: {
          currencyRef = cashRef;
          currencyImg = getIconURL();
          break;
        }
        case CurrencyTypes.Z: {
          currencyImg = SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI;
          currencyRef = zRef;
          break;
        }
        case CurrencyTypes.TICKETZ: {
          currencyImg = AssetHelper.pathForAssetNamed('ic_ticketz_background.png', false);
          currencyRef = ticketRef;
          break;
        }
      }
      const promise = new Promise(
        (resolve: (prop: IAnimatedShowerProps) => void, _reject: (error: Error) => void) => {
          const node = findNodeHandle(currencyRef);
          const currencyCap = configItem.currencyType == CurrencyTypes.CASH ? 200 : 250;
          if (!node) {
            console.warn('BalanceBarV2: Burst shower node undefined');
          }

          const getCurrencyCountNumber = (count: number | string): number => {
            if (typeof count === 'string') {
              return parseInt(count);
            }
            return count;
          };

          RCTUIManager.measure(
            node,
            (
              _x: number,
              _y: number,
              width: number,
              _height: number,
              pagePointX: number,
              pagePointY: number
            ) => {
              let endXOffset = 5;
              if (
                configItem.currencyType == CurrencyTypes.CASH &&
                Platform.OS == 'android' &&
                !ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
              ) {
                endXOffset = 10;
              }
              const widthXFallback = Number(pagePointX) + 7;
              const durationVCap = configItem.options.currencyCount < 50 ? 200 : 50;
              const decrementYpoint = ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
                ? 4
                : 1;
              const scaleItems = ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
                ? [0.4, 0.7, 1.1, 1]
                : [0.4, 0.7, 1, 0.7];
              const params = {
                icon: currencyImg,
                startingPoint: {x: configItem.options.x, y: configItem.options.y},
                endingPoint: {
                  x: width !== 0 ? Math.floor(pagePointX) - endXOffset : widthXFallback,
                  y: Math.floor(pagePointY) - decrementYpoint,
                },
                animationCount:
                  getCurrencyCountNumber(configItem.options.currencyCount) < currencyCap
                    ? getCurrencyCountNumber(configItem.options.currencyCount)
                    : currencyCap,
                animatedValue: {
                  startingVariant: {x: 20, y: 20},
                  durationV: durationVCap,
                  duration: 700,
                  delay: 300,
                  itemSize: 30,
                  scaleItems,
                  opacity: [0.5, 1, 1, 0],
                },
              };
              resolve(params);
            }
          );
        }
      );
      promises.push(promise);
    }
  );
  Promise.all(promises).then((showerArray: IAnimatedShowerProps[]) => {
    GlobalEventEmitter.emit('commitShowerAnimations', showerArray);
  });
};

export default class BalanceBar extends React.Component<Props, State> {
  flattenedStyle: ViewStyle;
  backgroundColor: any;
  animation: Animated.Value;
  scale: Animated.AnimatedInterpolation;
  currencyFlyingListener: any;
  currencyBurstFlyingListener: EmitterSubscription | undefined;

  lightThemeBorder: ViewStyle;
  darkThemeBorder: ViewStyle;

  static defaultProps = {
    shouldAnimate: true,
    borderType: 'none',
    currencyOrder: [CurrencyTypes.Z, CurrencyTypes.TICKETZ, CurrencyTypes.CASH],
    style: {height: constants.getBalanceBarHeight()},
    isTouchable: true,
    animationPrecisions: {z: 0, cash: 2, ticketz: 0},
    animationCurve: 'ease-in',
    animationDuration: 2000,
    identifier: '',
    forceAnimation: false,
    showRightSideIcon: false,
    showChatBadge: false,
    lightThemeOverride: false,
    darkThemeOverride: false,
  };

  constructor(props: Props) {
    super(props);
    this.flattenedStyle = StyleSheet.flatten([styles.balanceView, this.props.style]);
    this.backgroundColor = this.flattenedStyle.backgroundColor;
    this.animation = new Animated.Value(0);
    this.scale = this.animation.interpolate({
      inputRange: [0, 1],
      outputRange: [1, 1.15],
    });
    this.lightThemeBorder = this.props.lightThemeOverride ? styles.lightThemeOverride : {};
    this.darkThemeBorder = this.props.darkThemeOverride ? styles.darkThemeOverride : {};
  }

  componentDidMount() {
    if (!this.props.disableEvents) {
      this.currencyFlyingListener = GlobalEventEmitter.addListener(
        'requestShowerAnimation',
        (
          config: Array<{currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}>
        ) => {
          // @ts-ignore: Methods for refs not implemented
          requestShowerAnimation(config, this.cashRef, this.zCoinRef, this.ticketzRef);
        }
      );
      this.currencyBurstFlyingListener = GlobalEventEmitter.addListener(
        'requestBurstShowerAnimation',
        (
          config: Array<{currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}>
        ) => {
          // @ts-ignore: Methods for refs not implemented
          requestBurstShowerAnimation(config, this.cashRef, this.zCoinRef, this.ticketzRef);
        }
      );
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.showChatBadge != this.props.showChatBadge && this.props.showChatBadge) {
      this._animateChatButton();
    }
  }

  componentWillUnmount() {
    if (!this.props.disableEvents) {
      this.currencyFlyingListener?.remove();
      this.currencyFlyingListener = null;
      this.currencyBurstFlyingListener?.remove();
      this.currencyBurstFlyingListener = null;
    }
  }

  _animateChatButton() {
    Animated.sequence([
      Animated.timing(this.animation, {
        toValue: 1,
        useNativeDriver: false,
        duration: 300,
      }),
      Animated.timing(this.animation, {
        toValue: 0,
        useNativeDriver: false,
        duration: 300,
      }),
    ]).start();
  }

  _updateBalanceViewLayout(e: any) {
    if (e.persist) {
      e.persist();
    }

    if (!this.props.currencyLayout) {
      return;
    }

    const balanceLayout = e.nativeEvent.layout;
    balanceLayout.rootX = e.nativeEvent.layout.x;
    balanceLayout.rootY = 0;
    if (Sizes.isLandscape()) {
      if (Sizes.isTablet()) {
        balanceLayout.width = Sizes.getUIWidth();
        balanceLayout.height = 0;
      } else {
        balanceLayout.rootY = e.nativeEvent.layout.y + constants.MENU_HEIGHT;
        balanceLayout.width = 0;
        balanceLayout.height = e.nativeEvent.layout.height;
        balanceLayout.rootX = 0;
      }
    } else {
      balanceLayout.height = 0;
    }
    this.props.currencyLayout(balanceLayout);
  }

  _onChatButtonPress = () => {
    GlobalEventEmitter.emit('LaunchChatLobby');
    AWSManager.trackEvent(CURRENCY_BAR_CHAT);
  };

  _getTextStyle(): TextStyle {
    if (
      (this.props.lightThemeOverride || this.props.darkThemeOverride) &&
      this.props.style?.color
    ) {
      return {color: this.props.style.color};
    }
    return {};
  }

  _renderBalance(
    key: string,
    accessibilityLabel: string,
    testID: string,
    countingLabel: React.ReactElement,
    _index: number,
    containerStyle?: {}
  ): React.ReactElement {
    const borderStyle = styles['balanceBorder_' + this.props.borderType.toLowerCase()];
    const dividerColor = this.props.borderColor ? {borderColor: this.props.borderColor} : {};
    return (
      <View
        style={[styles.balanceContainer, containerStyle, borderStyle, dividerColor]}
        accessible={true}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={'button'}
        testID={testID}
        key={key}
      >
        <View style={styles.counterView}>
          <View style={styles.countingLabelContainer}>{countingLabel}</View>
        </View>
      </View>
    );
  }

  _renderTicketzBalance(index: number): React.ReactElement {
    let onPressFunction: null | (() => void) | (() => {}) = null;
    if (this.props.isTouchable) {
      onPressFunction = () => {
        NavigationManager.displayTicketz();
        AWSManager.trackEvent(CURRENCY_BAR_TICKETZ);
        trackTelemetryEvent({
          event: TelemetryEventName.TICKETZ_STORE_LOAD,
          includeUserWalletDetails: true,
        });
      };
    }

    return this._renderBalance(
      'ticketz',
      I18n.message('Ticketz balance {0}', this.props.ticketzBalance),
      'ticketz_balance',
      <TicketzCountingLabel
        style={[styles.countingLabel, this._getTextStyle()]}
        containerStyle={
          this.props.style?.backgroundColor
            ? [
                this.lightThemeBorder,
                this.darkThemeBorder,
                {backgroundColor: this.props.style?.backgroundColor},
              ]
            : [this.lightThemeBorder, this.darkThemeBorder]
        }
        ticketzLayout={(_e: any) => {
          /*RN Measure Workaround for Android*/
        }}
        value={this.props.ticketzBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.ticketz}
        ticketzRef={(ref: any) => {
          this.ticketzRef = ref;
        }}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
        onPressFunc={onPressFunction}
      />,
      index
    );
  }

  _renderZBalance(index: number): React.ReactElement {
    let onPressFunction: null | (() => void) | (() => {}) = null;
    if (this.props.isTouchable) {
      onPressFunction = () => {
        NavigationManager.popToRoot();
        NativeNavigation.gotoDestination('Store');
        AWSManager.trackEvent(CURRENCY_BAR_Z);
      };
    }

    return this._renderBalance(
      'z',
      I18n.message('Z balance {0}', this.props.zBalance),
      'z_balance',
      <ZCountingLabel
        style={[styles.countingLabel, this._getTextStyle()]}
        containerStyle={
          this.props.style?.backgroundColor
            ? [
                this.lightThemeBorder,
                this.darkThemeBorder,
                {backgroundColor: this.props.style?.backgroundColor},
              ]
            : [this.lightThemeBorder, this.darkThemeBorder]
        }
        coinLayout={(_e: any) => {
          /*RN Measure Workaround for Android*/
        }}
        coinRef={(ref: any) => {
          this.zCoinRef = ref;
        }}
        value={this.props.zBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.z}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
        onPressFunc={onPressFunction}
      />,
      index
    );
  }

  _renderCashBalance(index: number): React.ReactElement | null {
    let onPressFunction: null | (() => void) | (() => {}) = null;
    if (this.props.isTouchable) {
      onPressFunction = () => {
        NavigationManager.popToRoot();
        NativeNavigation.gotoDestination('Store');
        AWSManager.trackEvent(CURRENCY_BAR_CASH);
        trackTelemetryEvent({
          event: TelemetryEventName.LOBBY_WALLET_ICON_CLICK,
          includeUserWalletDetails: true,
        });
      };
    }

    if (ClientPropertyConstants.getIsVirtualCurrencyOnly()) {
      return null;
    }

    return this._renderBalance(
      'cash',
      I18n.message('Cash balance {0}', I18n.getCurrencyString(this.props.cashBalance, '$')),
      'cash_balance',
      <CashCountingLabel
        style={[styles.countingLabel, this._getTextStyle()]}
        containerStyle={
          this.props.style?.backgroundColor
            ? [
                this.lightThemeBorder,
                this.darkThemeBorder,
                {backgroundColor: this.props.style?.backgroundColor},
              ]
            : [this.lightThemeBorder, this.darkThemeBorder]
        }
        cashLayout={(_e: any) => {
          /*RN Measure Workaround for Android*/
        }}
        cashRef={(ref: any) => {
          this.cashRef = ref;
        }}
        value={this.props.cashBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.cash}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
        onPressFunc={onPressFunction}
      />,
      index
    );
  }

  _renderMedalBalance(index: number): React.ReactElement {
    return this._renderBalance(
      'medals',
      I18n.message('Medal balance {0}', this.props.medalBalance),
      'medal_balance',
      <MedalCountingLabel
        style={[styles.countingLabel, this._getTextStyle()]}
        containerStyle={
          this.props.style?.backgroundColor
            ? [
                this.lightThemeBorder,
                this.darkThemeBorder,
                {backgroundColor: this.props.style?.backgroundColor},
              ]
            : [this.lightThemeBorder, this.darkThemeBorder]
        }
        medalLayout={(_e: any) => {
          /*RN Measure Workaround for Android*/
        }}
        value={this.props.medalBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.ticketz}
        medalRef={(ref: any) => {
          this.medalRef = ref;
        }}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
      />,
      index
    );
  }

  _renderBalanceForType(type: string, index: number): React.ReactElement | null {
    switch (type) {
      case CurrencyTypes.CASH:
        return this._renderCashBalance(index);
      case CurrencyTypes.TICKETZ:
        return this._renderTicketzBalance(index);
      case CurrencyTypes.Z:
        return this._renderZBalance(index);
      case CurrencyTypes.MEDAL:
        return this._renderMedalBalance(index);
      default:
        return <View />;
    }
  }

  _onChatLayout(event: LayoutChangeEvent) {
    if (this.props.educationChatRef && this.props.onEducationChatSpotlightUpdated) {
      const node = findNodeHandle(this.props.educationChatRef.current);
      if (event?.nativeEvent?.layout && node) {
        RCTUIManager.measure(
          node,
          (
            _tileX: number,
            _tileY: number,
            _tileWidth: number,
            tileHeight: number,
            _tilePageX: number,
            _tilePageY: number
          ) => {
            const spotlightRefProps = {
              spotlightCornerRadiusChat: tileHeight,
              spotlightRefChat: node,
            };
            this.props.onEducationChatSpotlightUpdated(spotlightRefProps);
          }
        );
      }
    }
  }

  _renderChatIcon(): React.ReactElement {
    return (
      <View style={styles.chatContainer}>
        <TouchableOpacity testID={TEST_IDS.CHAT_BUTTON} onPress={this._onChatButtonPress}>
          <View ref={this.props.educationChatRef} onLayout={this._onChatLayout.bind(this)}>
            <Animated.View style={{transform: [{scale: this.scale}]}}>
              <SkillzSVG style={styles.chatIcon} source={chatIconSvg} />
            </Animated.View>
            <Optional test={this.props.showChatBadge}>
              <View style={styles.badge} testID={TEST_IDS.CHAT_BADGE} />
            </Optional>
          </View>
        </TouchableOpacity>
      </View>
    );
  }

  _renderSkillzPowered(): React.ReactElement {
    const tintColorForTheme = pickTextColorBasedOnBgColor(
      this.flattenedStyle.backgroundColor as string,
      '#FFFFFF',
      '#3A3A3A'
    );
    return (
      <SkillzImage
        style={styles.skillzPowered}
        resizeMode={'contain'}
        source={'ic_skillz_powered_v2.png'}
        tintColor={tintColorForTheme}
      />
    );
  }

  _renderRightSideIcon(): React.ReactElement | undefined {
    if (this.props.showRightSideIcon) {
      if (ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()) {
        return this._renderSkillzPowered();
      }
      return this._renderChatIcon();
    }
    return;
  }

  render(): React.ReactElement {
    return (
      <View
        testID={TEST_IDS.BALANCE_BAR}
        style={this.flattenedStyle}
        onLayout={this._updateBalanceViewLayout.bind(this)}
      >
        <View style={styles.currencyContainer}>
          {this.props.currencyOrder.map(
            (type: string, index: number): React.ReactElement | null => {
              return this._renderBalanceForType(type, index);
            }
          )}
        </View>
        {this._renderRightSideIcon()}
      </View>
    );
  }
}
